/**
 * One Lupa conversation store for the whole app (07-hubs §5).
 *
 * The Lupa Hub (full chat page, App.tsx) and the floating Lupa widget both read
 * and write this store, so a conversation started in the widget on one Hub is
 * the same conversation on the next Hub and in the full page ("Open in Lupa").
 * It is mounted once at the app root, above the router's pages, so it survives
 * navigation. It is keyed on the signed-in user (see AppRoutes), so signing out
 * drops the previous user's conversations.
 *
 * Persistence is unchanged: the backend /chat endpoint saves conversations;
 * this store loads them once and keeps the client-side copy in sync.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatResponse, Conversation, Message } from '../types';
import { apiService } from '../services/api';
import { generateUUID } from '../utils/uuid';
import {
  DEFAULT_AGENT_ID,
  DEFAULT_CONVERSATION_TITLE,
  createEmptyConversation,
  deriveConversationTitle,
  readStoredClientId,
  sortConversationsByUpdatedAt,
  storeClientId,
} from './lupaChatHelpers';

export interface LupaChatStore {
  conversations: Conversation[];
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  currentConversationId: string;
  setCurrentConversationId: React.Dispatch<React.SetStateAction<string>>;
  currentConversation: Conversation | undefined;
  currentAgent: string;
  setCurrentAgent: React.Dispatch<React.SetStateAction<string>>;
  selectedClientId: string;
  /** Sets the chat client filter and remembers it (localStorage `selectedClientId`). */
  setSelectedClientId: (id: string) => void;
  isLoading: boolean;
  conversationsLoaded: boolean;
  /**
   * Send a message in the current conversation. Appends the user message, then
   * Lupa's reply (or an error reply). Returns the response, or undefined if
   * nothing was sent or the request failed.
   */
  sendMessage: (content: string) => Promise<ChatResponse | undefined>;
  /** Make a conversation current (restores its agent and client). */
  selectConversation: (conversationId: string) => void;
  /** Start an empty conversation and make it current. */
  startNewConversation: (opts?: { communityLabel?: string; clientId?: string; agentId?: string }) => Conversation;
}

const LupaChatContext = createContext<LupaChatStore | undefined>(undefined);

export const LupaChatProvider: React.FC<{ enabled: boolean; children: React.ReactNode }> = ({
  enabled,
  children,
}) => {
  const [currentAgent, setCurrentAgent] = useState(DEFAULT_AGENT_ID);
  const [initialConversation] = useState(() =>
    createEmptyConversation(DEFAULT_AGENT_ID, undefined, readStoredClientId())
  );
  const [conversations, setConversations] = useState<Conversation[]>([initialConversation]);
  const [currentConversationId, setCurrentConversationId] = useState(initialConversation.id);
  const [selectedClientId, setSelectedClientIdState] = useState<string>(readStoredClientId);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const loadStarted = useRef(false);

  const setSelectedClientId = useCallback((id: string) => {
    setSelectedClientIdState(id);
    storeClientId(id);
  }, []);

  const currentConversation = useMemo(() => {
    if (conversations.length === 0) return undefined;
    return conversations.find((c) => c.id === currentConversationId) ?? conversations[0];
  }, [conversations, currentConversationId]);

  // Load persisted conversations once, for DWA users only (client-role users have no chat).
  useEffect(() => {
    if (!enabled || loadStarted.current) return;
    loadStarted.current = true;
    const load = async () => {
      try {
        const stored = await apiService.getConversations(100, 0, true);
        if (stored.length > 0) {
          const sorted = sortConversationsByUpdatedAt(stored);
          setConversations(sorted);
          setCurrentConversationId(sorted[0].id);
          setCurrentAgent(sorted[0].agentId);
          setSelectedClientId(sorted[0].clientId ?? 'dwa-internal');
        }
      } catch (error) {
        console.error('Failed to load conversations:', error);
      } finally {
        setConversationsLoaded(true);
      }
    };
    load();
  }, [enabled, setSelectedClientId]);

  // Refs so the async send below always reads the latest values.
  const isLoadingRef = useRef(false);
  const latest = useRef({ conversations, currentConversation, currentAgent, selectedClientId });
  latest.current = { conversations, currentConversation, currentAgent, selectedClientId };

  const appendToConversation = useCallback((conversationId: string, message: Message) => {
    setConversations((prev) =>
      sortConversationsByUpdatedAt(
        prev.map((c) =>
          c.id === conversationId
            ? { ...c, messages: [...c.messages, message], updatedAt: message.timestamp }
            : c
        )
      )
    );
  }, []);

  const sendMessage = useCallback(
    async (content: string): Promise<ChatResponse | undefined> => {
      const trimmed = content.trim();
      const { currentConversation: conversation, currentAgent: agent, selectedClientId: clientId } =
        latest.current;
      if (!trimmed || isLoadingRef.current || !conversation) return undefined;

      const conversationId = conversation.id;
      const userTimestamp = new Date();
      const userMessage: Message = {
        id: generateUUID(),
        role: 'user',
        content: trimmed,
        timestamp: userTimestamp,
      };

      setConversations((prev) =>
        sortConversationsByUpdatedAt(
          prev.map((c) => {
            if (c.id !== conversationId) return c;
            const shouldAutoTitle = c.messages.length === 0 && c.title === DEFAULT_CONVERSATION_TITLE;
            return {
              ...c,
              title: shouldAutoTitle ? deriveConversationTitle(trimmed) : c.title,
              messages: [...c.messages, userMessage],
              updatedAt: userTimestamp,
            };
          })
        )
      );

      isLoadingRef.current = true;
      setIsLoading(true);
      try {
        const response = await apiService.sendMessage(
          agent,
          trimmed,
          conversation.sessionId,
          conversation.communityLabel || undefined,
          clientId
        );
        appendToConversation(conversationId, {
          id: generateUUID(),
          role: 'assistant',
          content: response.response,
          timestamp: new Date(),
        });
        return response;
      } catch {
        appendToConversation(conversationId, {
          id: generateUUID(),
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
        });
        return undefined;
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [appendToConversation]
  );

  const selectConversation = useCallback(
    (conversationId: string) => {
      setCurrentConversationId(conversationId);
      const selected = latest.current.conversations.find((c) => c.id === conversationId);
      if (selected) {
        setCurrentAgent(selected.agentId);
        setSelectedClientId(selected.clientId ?? 'dwa-internal');
      }
    },
    [setSelectedClientId]
  );

  const startNewConversation = useCallback(
    (opts: { communityLabel?: string; clientId?: string; agentId?: string } = {}) => {
      const label = opts.communityLabel && opts.communityLabel.trim().length > 0 ? opts.communityLabel : undefined;
      const conversation = createEmptyConversation(
        opts.agentId ?? latest.current.currentAgent,
        label,
        opts.clientId ?? latest.current.selectedClientId
      );
      setConversations((prev) => sortConversationsByUpdatedAt([conversation, ...prev]));
      setCurrentConversationId(conversation.id);
      return conversation;
    },
    []
  );

  const value = useMemo<LupaChatStore>(
    () => ({
      conversations,
      setConversations,
      currentConversationId,
      setCurrentConversationId,
      currentConversation,
      currentAgent,
      setCurrentAgent,
      selectedClientId,
      setSelectedClientId,
      isLoading,
      conversationsLoaded,
      sendMessage,
      selectConversation,
      startNewConversation,
    }),
    [
      conversations,
      currentConversationId,
      currentConversation,
      currentAgent,
      selectedClientId,
      setSelectedClientId,
      isLoading,
      conversationsLoaded,
      sendMessage,
      selectConversation,
      startNewConversation,
    ]
  );

  return <LupaChatContext.Provider value={value}>{children}</LupaChatContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export function useLupaChat(): LupaChatStore {
  const ctx = useContext(LupaChatContext);
  if (!ctx) throw new Error('useLupaChat must be used inside <LupaChatProvider>');
  return ctx;
}
